import { FC, MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { SkeletonTheme } from 'react-loading-skeleton'
import { ToastContainer } from 'react-toastify'
import { RouterProvider } from 'react-router-dom'
import { Amplify } from 'aws-amplify'
import '@aws-amplify/ui-react/styles.css'
import awsconfig from 'app/aws-exports'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectAuthState,
    selectConsultState,
    selectModalState,
} from 'app/store/selectors'
import { router } from 'app/router'
import { Theme, GlobalModal } from '@tuhi/components'
import { closeModal, openModal } from './store/slices/modalSlice'
import { AwsCredentialsProvider } from './api/auth/AwsCredentialsProvider'
import { MsalProvider } from '@azure/msal-react'
import { initialiseMsal, msalInstance } from './api/auth'
import { useUserActivityListener } from './utils/userActivityListener'
import { AreYouThereModal } from './globalModals/areYouThereModal'
import { flushOfflineLogs } from './logging/firebase'
import { enableMapSet } from 'immer'
import Modal from 'react-modal'
import { OfflineIndicator } from './components/OfflineIndicator'

export const awsCredentialsProvider = new AwsCredentialsProvider()

// The tuhiDev REST API is CDK-owned, so its endpoint is no longer present in the
// Amplify-generated aws-exports (aws_cloud_logic_custom) once Amplify stops managing it.
// Inject it from a per-env env var so REST calls (e.g. /userInfo, QR auth) keep working.
// Guarded: if the env var is unset (e.g. an env still pulling the endpoint from aws-exports),
// the existing config is left untouched.
const restApiEndpoint = process.env.REST_API_ENDPOINT
if (restApiEndpoint) {
    ;(awsconfig as Record<string, unknown>).aws_cloud_logic_custom = [
        {
            name: 'tuhiDev',
            endpoint: restApiEndpoint,
            region: 'ap-southeast-2',
        },
    ]
}

Amplify.configure(awsconfig, {
    // Amplify v6 has no Analytics library option (the old `disabled` flag was
    // ignored); development analytics are skipped in logAnalytics instead.
    Auth: {
        credentialsProvider: awsCredentialsProvider,
    },
})

Modal.setAppElement('#root')
enableMapSet()
initialiseMsal()

export const AmplifyApp: FC = () => {
    const { isAuthenticated } = useSelector(selectAuthState)
    const { isOpen, style, children } = useSelector(selectModalState)
    const { isRunningConsult, isPlayingAudio } = useSelector(selectConsultState)
    const areYouThereTimerRef = useRef<NodeJS.Timeout | null>(null)

    const dispatch = useDispatch()
    const timeoutInMinutes = 20
    const signOutInMinutes = 2

    const createAreYouThereModalTimer = useCallback(
        (callback: () => void) => {
            const modalTimer = (timeoutInMinutes - signOutInMinutes) * 60 * 1000
            return setTimeout(callback, modalTimer)
        },
        [timeoutInMinutes, signOutInMinutes]
    )

    const showAreYouThereModal = useCallback(
        (timerRef: MutableRefObject<NodeJS.Timeout | null>) => {
            if (!timerRef.current) {
                return
            }

            dispatch(closeModal())

            clearTimeout(timerRef.current)
            timerRef.current = null

            dispatch(
                openModal({
                    children: (
                        <AreYouThereModal
                            signOutInMinutes={signOutInMinutes}
                            onSignOut={unsubscribeUserActivityListener}
                            onResume={resetTimeoutTimer}
                        />
                    ),
                })
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resetTimeoutTimer and unsubscribeUserActivityListener are declared after this callback and resetTimeoutTimer calls it; the modal reads them when it is opened
        [dispatch, signOutInMinutes]
    )

    const { subscribeToUserActivity, unsubscribeUserActivityListener } =
        useUserActivityListener(() => {
            // TODO: use throttle/debounce to reduce number of timer recreation
            if (areYouThereTimerRef.current) {
                clearTimeout(areYouThereTimerRef.current ?? undefined)
                areYouThereTimerRef.current = createAreYouThereModalTimer(
                    () => {
                        showAreYouThereModal(areYouThereTimerRef)
                    }
                )
            }
        })

    const resetTimeoutTimer = useCallback(() => {
        areYouThereTimerRef.current = createAreYouThereModalTimer(() => {
            showAreYouThereModal(areYouThereTimerRef)
        })
        subscribeToUserActivity()
    }, [
        createAreYouThereModalTimer,
        showAreYouThereModal,
        subscribeToUserActivity,
    ])

    const clearTimeoutTimer = useCallback(() => {
        clearTimeout(areYouThereTimerRef.current ?? undefined)
        areYouThereTimerRef.current = null
        unsubscribeUserActivityListener()
    }, [unsubscribeUserActivityListener])

    useEffect(() => {
        flushOfflineLogs()
    }, [])

    useEffect(() => {
        if (!isAuthenticated) {
            clearTimeoutTimer()
            return
        }

        if (
            (isRunningConsult || isPlayingAudio) &&
            areYouThereTimerRef.current
        ) {
            clearTimeoutTimer()
        } else if (
            !isRunningConsult &&
            !isPlayingAudio &&
            !areYouThereTimerRef.current
        ) {
            resetTimeoutTimer()
        }

        return () => {
            unsubscribeUserActivityListener()
        }
    }, [
        isAuthenticated,
        isRunningConsult,
        isPlayingAudio,
        clearTimeoutTimer,
        resetTimeoutTimer,
        unsubscribeUserActivityListener,
    ])

    return (
        <Theme>
            <SkeletonTheme>
                <OfflineIndicator />
                <ToastContainer style={{ top: 100 }} />
                <MsalProvider instance={msalInstance}>
                    <RouterProvider router={router} />
                </MsalProvider>
                <GlobalModal
                    {...{ isOpen, style, children }}
                    onRequestClose={() => dispatch(closeModal())}
                />
            </SkeletonTheme>
        </Theme>
    )
}
